import { KeyVaultApiService } from '@core/services/api.services/key-vault-api.service';
import { CustomSnackbarService } from '@core/services/custom-snackbar.service';
import { Injectable } from '@angular/core';
import { BehaviorSubject, interval, Observable, Subject } from 'rxjs';
import { MemberProfileApiService } from '@core/services/api.services/member-profile-api.service';
import {
  MemberProfileDto,
  MemberSyncDto,
  PaginatedResponseDto,
  ProductDto,
  ProductTypeDto,
} from '@core/services/dto';
import { filter, map, switchMap, finalize, tap, take, shareReplay } from 'rxjs/operators';
import {
  AccountsApiService,
  OfferApiService,
  ProductApiService,
  ConversationApiService,
  EmailTemplatesApiService,
  PersonApiService,
} from '@core/services/api.services';
import { CampaignsDto } from '@core/services/dto/campaigns.dto';
import { SpinnerService } from '@shared/services/spinner.service';
import {
  CampaignsModel,
  MemberProfileModel,
  MemberSyncModel,
  ProductModel,
  ProductTypeModel,
  SearchAccountOptionsModel,
  UserNameModel,
  SiteDetailModel,
  SearchUserOptionsModel,
  SendEmailRequestModel,

} from '@shared/models';
import { LinkSearchRequestDto } from '@shared/models/requests/link-search.request.dto';
import { BankIntegrationConfigurationDto, CoreBankingIntegrationListDto } from '@shared/models/bank-integration-configuration.dto';
import { ApplicationsApiService } from '@core/services/api.services/applications-api.service';
import { UserBranchDto } from './dto/user-Branch.dto';
import { UserBranchModel } from '@shared/models/user-Branch.model';
import { ProspectProfileApiService } from './api.services/prospect-profile-api.service';
import { ProspectCreate } from '@shared/models/prospect-create';
import { HttpErrorResponse } from '@angular/common/http';
import { getOfferApiBase } from '@config/org.config';
import { ConcernTypeModel } from '@shared/models/concern-type.models';
import { ConcernTypeDto } from './dto/concern-type.dto';
import { SendEmailRequestDto } from '@shared/models/requests/send.email.request.dto';
import { ECMProductModel } from '@shared/models/ecm-product.model';
import { ECMProductTypeModel } from '@shared/models/ecm-product-type.model';
import { ApplicationViewApiService } from './api.services/application-view-api.service';
import { ApplicationMenuViewItemModel } from '@shared/models/application-view.model';
import { ApplicationMenuViewItemDto } from './dto/Application-view-menu.dto';
import { ApiResponse, ApplicationApiResponse } from './dto/api-response.dto';
import { ApplicationAssetResponse } from './dto/application-asset-response.dto';
import { ApplicationAssetModel } from '@shared/models/application-asset.model';
import { ApplicationAssetsModule } from 'app/modules/Application-View/Pages/application-assets/application-assets.module';
import { AssetCategoryResponse } from './dto/asset-type-response.dto';
import { EquifaxConfigurations } from './dto/EquifaxConfiguration.dto';
import { SiteDetailDto, EquifaxCreditProvider } from './dto/site-detail.dto';
import { GLOBAL_SETTINGS } from '@shared/constants/global-settings';

const offerBase = getOfferApiBase();

@Injectable({
  providedIn: 'root',
})
export class GlobalStateService {
  private productTypes: BehaviorSubject<ProductTypeModel[]> =
    new BehaviorSubject<ProductTypeModel[]>([]);
  private concernTypes: BehaviorSubject<ConcernTypeModel[]> =
    new BehaviorSubject<ConcernTypeModel[]>([]);
  private concernType: BehaviorSubject<ConcernTypeModel[]> =
    new BehaviorSubject<ConcernTypeModel[]>([]);
  private campaigns: BehaviorSubject<CampaignsModel[]> = new BehaviorSubject<
    CampaignsModel[]
  >([]);
  private userBranches: BehaviorSubject<UserBranchModel[]> = new BehaviorSubject<
    UserBranchModel[]
  >([]);
  public products: BehaviorSubject<ProductModel[]> = new BehaviorSubject<
    ProductModel[]
  >([]);
  private bankIntegrationConfigurationSubject: BehaviorSubject<BankIntegrationConfigurationDto> =
    new BehaviorSubject(null);

  private coreBankingIntegrationListSubject: BehaviorSubject<CoreBankingIntegrationListDto> =
    new BehaviorSubject(null);

  private sqlBuilderPageVisibleSubject: BehaviorSubject<boolean> =
    new BehaviorSubject(null);
  private ECMUsageKeySubject: BehaviorSubject<boolean> = new BehaviorSubject(
    null
  );
  private PendoUsageKeySubject: BehaviorSubject<string> = new BehaviorSubject(
    null
  );
  private themeBuilderTypeKeySubject: BehaviorSubject<string> =
    new BehaviorSubject(null);

  private powerBiUrlSubject: BehaviorSubject<string> = new BehaviorSubject(null);


  private useDefaultBannerSource = new BehaviorSubject<boolean>(false);
  useDefaultBanner$ = this.useDefaultBannerSource.asObservable();

  setUseDefaultBanner(value: boolean) {
    this.useDefaultBannerSource.next(value);}

  private ECMUsageKeyLoading: boolean;
  private PendoUsageKeyLoading: string;
  private sqlBuilderUsageKeyLoading: boolean;
  private themeBuilderTypeKeyLoading: boolean;
  public conversationRoutePath = 'conversationRoutePath';
  public salesRoutePath = 'salesRoutePath';
  public interactionRoutePath = 'interactionRoutePath';
  public NotesRoutePath = 'notesRoutePath';
  public TaskRoutePath = 'tasksRoutePath';
  public appNoteUpdated: Subject<boolean> = new Subject<boolean>();
  public appNoteUpdated$: Observable<boolean> = this.appNoteUpdated.asObservable();
  public conditionsUpdated: Subject<boolean> = new Subject<boolean>();
  public conditionsUpdated$: Observable<boolean> = this.conditionsUpdated.asObservable();
  public termUpdated: Subject<boolean> = new Subject<boolean>();
  public termUpdated$: Observable<boolean> = this.termUpdated.asObservable();
  public feesUpdated: Subject<boolean> = new Subject<boolean>();
  public feesUpdated$: Observable<boolean> = this.feesUpdated.asObservable();
  public productUpdated: Subject<boolean> = new Subject<boolean>();
  public productUpdated$: Observable<boolean> = this.productUpdated.asObservable();
  public noteUpdated: Subject<boolean> = new Subject<boolean>();
  public noteUpdated$: Observable<boolean> = this.noteUpdated.asObservable();
  public saleAdded: Subject<boolean> = new Subject<boolean>();
  public saleAdded$: Observable<boolean> = this.saleAdded.asObservable();
  public autoDecisionSubject = new BehaviorSubject<any>(null);
  public autoDecision$ = this.autoDecisionSubject.asObservable();
  public rateAutoDecisionSubject = new BehaviorSubject<any>(null);
  public rateAutoDecision$ = this.rateAutoDecisionSubject.asObservable();
  public approvalVersionSubject = new BehaviorSubject<any>(null);
  public approvalVersion$ = this.approvalVersionSubject.asObservable();
  public rateVersionSubject = new BehaviorSubject<any>(null);
  public rateVersion$ = this.rateVersionSubject.asObservable();
    public equifaxConfigurationSubject = new BehaviorSubject<any>(null);
  public equifaxConfiguration$ = this.equifaxConfigurationSubject.asObservable();

  /** Cached site details (single API call shared by all consumers). */
  private siteDetailsCache$: Observable<SiteDetailDto>;
  /** Credit report provider from site details (EquifaxCanada | EquifaxUS). Use in credit report UI to show/hide sections. */
  public creditReportProvider$: Observable<EquifaxCreditProvider>;
  public taskUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  public interactionUpdated: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(
    false
  );
  public conversationUpdated: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(false);
  public memberProfile: BehaviorSubject<MemberProfileModel> =
    new BehaviorSubject<MemberProfileModel>(null);
  public prospectProfile: BehaviorSubject<ProspectCreate> =
    new BehaviorSubject<ProspectCreate>(null);
  public preselectedAccount: BehaviorSubject<SearchAccountOptionsModel[]> =
    new BehaviorSubject<SearchAccountOptionsModel[]>(null);
  public preselectedAccount$: Observable<SearchAccountOptionsModel[]> =
    this.preselectedAccount.asObservable();
  public user: UserNameModel;
  public userGroup: SearchUserOptionsModel;
  // Added fields to manage global variables for licenses
  public IsBankIOLicense: boolean;
  public IsEcmLicense: boolean;
  public IsCrmLicense: boolean;
  public IsOriginationLicense: boolean;
  public userEmail: string;
  private importResponse: BehaviorSubject<MemberSyncModel> =
    new BehaviorSubject<MemberSyncModel>(null);
  private UmbrellaVersionSubject: BehaviorSubject<string> = new BehaviorSubject(
    null
  );
  private UmbrellaVersionLoding: string;
  public siteDetail: BehaviorSubject<SiteDetailModel> =
    new BehaviorSubject<SiteDetailModel>(null);

  public offerSiteDetail: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);

  public offerEnableLogic: BehaviorSubject<number> =
    new BehaviorSubject<number>(null);

  public isOfferLicence: BehaviorSubject<boolean> =
    new BehaviorSubject<boolean>(null);
  isProgress = false;
  public applicationView: BehaviorSubject<ApplicationMenuViewItemModel> =
  new BehaviorSubject<ApplicationMenuViewItemModel>(null);
  public aseetdetails: BehaviorSubject<ApplicationAssetResponse> =
  new BehaviorSubject<ApplicationAssetModel>(null);
  public allAssetTypes: BehaviorSubject<AssetCategoryResponse[]> = new BehaviorSubject<AssetCategoryResponse[]>([]);
  public CUName = new BehaviorSubject('');
  public refreshBatchAssigneeTask = new BehaviorSubject(null);
  public refreshBatchAssigneeTask$ = this.refreshBatchAssigneeTask.asObservable().pipe(filter((x) => !!x));
public isDataWarehouseEnable = localStorage.getItem(GLOBAL_SETTINGS.dataWarehouseStatus) !== null ? localStorage.getItem(GLOBAL_SETTINGS.dataWarehouseStatus) : false;
  public importResponse$: Observable<MemberSyncModel> = this.importResponse
    .asObservable()
    .pipe(filter((x) => !!x));

  public bankIntegrationConfiguration$ =
    this.bankIntegrationConfigurationSubject.asObservable().pipe(
      tap((config) => {
       if (!config && this.isDataWarehouseEnable === '"False"') {
          this.loadBankIntegrationConfiguration();
        }
      }),
      filter((config) => !!config)
    );

  public coreBankingIntegrationList$ =
    this.coreBankingIntegrationListSubject.asObservable().pipe(
      tap((config) => {
        if (!config) {
          this.loadCoreBankingIntegrationList();
        }
      }),
      filter((config) => !!config)
    );

  public sqlBuilderPageVisible$ = this.sqlBuilderPageVisibleSubject
    .asObservable()
    .pipe(
      tap((visible) => {
        if (
          !this.sqlBuilderUsageKeyLoading &&
          (visible === undefined || visible === null)
        ) {
          this.loadSqlBuilderUsageKey();
        }
      }),
      filter((visible) => !(visible === undefined || visible === null))
    );

  public offerVisible$ = this.offerSiteDetail.asObservable().pipe(
    tap((visible) => {
      if (visible === undefined || visible === null) {
        this.getOfferSite();
      }
    }),
    filter((visible) => !(visible === undefined || visible === null))
  );

  public offerLicence$ = this.isOfferLicence.asObservable().pipe(
    tap((visible) => {
      if (visible === undefined || visible === null) {
        this.getOfferLicence();
      }
    }),
    filter((visible) => !(visible === undefined || visible === null))
  );

  public offerEnableLogic$ = this.offerEnableLogic.asObservable().pipe(
    tap((visible) => {
      if (visible === undefined || visible === null) {
        this.getOfferEnableLogic();
      }
    }),
    filter((visible) => !(visible === undefined || visible === null))
  );

  public ECMVisible$ = this.ECMUsageKeySubject.asObservable().pipe(
    tap((visible) => {
      if (
        !this.ECMUsageKeyLoading &&
        (visible === undefined || visible === null)
      ) {
        this.loadECMUsageKey();
      }
    }),
    filter((visible) => !(visible === undefined || visible === null))
  );

  public themeBuilderType$ = this.themeBuilderTypeKeySubject
    .asObservable()
    .pipe(
      tap((type) => {
        if (
          !this.themeBuilderTypeKeyLoading &&
          (type === undefined || type === null)
        ) {
          this.loadThemeBuilderKey();
        }
      }),
      filter((type) => !(type === undefined || type === null))
    );

  public VersionVisible$ = this.UmbrellaVersionSubject.asObservable().pipe(
    tap((visible) => {
      if (
        !this.UmbrellaVersionLoding &&
        (visible === undefined || visible === null)
      ) {
        this.loadUmbrellaVersion();
        this.getOfferSite();
      }
    }),
    filter((visible) => !(visible === undefined || visible === null))
  );

  public PendoAPIKey$ = this.PendoUsageKeySubject.asObservable().pipe(
    tap((visible) => {
      if (
        !this.PendoUsageKeyLoading &&
        (visible === undefined || visible === null)
      ) {
        this.loadPendoUsageKey();
      }
    }),
    filter((visible) => !(visible === undefined || visible === null))
  );

  public powerBiUrl$ = this.powerBiUrlSubject.asObservable().pipe(
    tap((url) => {
      if (url === undefined || url === null) {
        this.loadPowerBiUrl();
      }
    }),
    filter((url) => !(url === undefined || url === null))
  );
  constructor(
    private memberProfileApiService: MemberProfileApiService,
    private prospectProfileApiService: ProspectProfileApiService,
    private productService: ProductApiService,
    private conversationService: ConversationApiService,
    private accountsService: AccountsApiService,
    private spinnerService: SpinnerService,
    private toastService: CustomSnackbarService,
    private keyVaultService: KeyVaultApiService,
    private asappApiService: ApplicationsApiService,
    private offerApiService: OfferApiService,
    private personApiService: PersonApiService,
    private emailsService: EmailTemplatesApiService,
    private applicationService: ApplicationViewApiService,

  ) {
    this.siteDetailsCache$ = this.asappApiService.getSiteDetails().pipe(shareReplay(1));
    this.creditReportProvider$ = this.siteDetailsCache$.pipe(map((dto) => dto?.creditReportProvider));
  }
  /**
   * Returns site id from localStorage (siteConfiguration).
   *
   * @returns Site id string or null if not found.
   */
  getSiteIdFromLocalStorage(): string | null {
    try {
      const raw = localStorage.getItem('siteConfiguration');
      if (!raw) {return null;}
      const config = JSON.parse(raw) as { siteId?: string };
      return config?.siteId ?? null;
    } catch {
      return null;
    }
  }
  memberProfile$(): Observable<MemberProfileModel> {
    return this.memberProfile.asObservable().pipe(filter((x) => !!x));
  }

  productTypes$(): Observable<ProductTypeModel[]> {
    return this.productTypes.asObservable();
  }

  products$(): Observable<ProductModel[]> {
    return this.products.asObservable();
  }


  getproductTypes$(): Observable<ProductTypeModel[]> {
    return this.productTypes.asObservable();
  }

  getproducts$(): Observable<ProductModel[]> {
    return this.products.asObservable();
  }


  campaigns$(): Observable<CampaignsModel[]> {
    return this.campaigns.asObservable();
  }

  userBranches$(): Observable<UserBranchModel[]> {
    return this.userBranches.asObservable();
  }

  taskUpdated$(): Observable<boolean> {
    return this.taskUpdated.asObservable();
  }

  interactionUpdated$(): Observable<boolean> {
    return this.interactionUpdated.asObservable();
  }

  conversationUpdated$(): Observable<boolean> {
    return this.conversationUpdated.asObservable();
  }

  concernTypes$(): Observable<ConcernTypeModel[]> {
    return this.concernTypes.asObservable();
  }
  concernType$(): Observable<ConcernTypeModel[]> {
    return this.concernType.asObservable();
  }
  applicationViewDetails$(): Observable<ApplicationMenuViewItemModel> {
    return this.applicationView.asObservable().pipe(filter((x) => !!x));
  }
    applicationAsset$(): Observable<ApplicationAssetModel> {
    return this.aseetdetails.asObservable().pipe(filter((x) => !!x));
  }

  getAllAssetTypes$(): Observable<AssetCategoryResponse[]> {
    return this.allAssetTypes.asObservable();
  }

  getMemberProfile(memberId: number): void {
    this.memberProfileApiService
      .getMemberProfileInfo(memberId)
      .subscribe((data: MemberProfileDto) => {
        this.memberProfile.next(new MemberProfileModel(data));
      });
  }

  getProspectProfile(memberId: number): void {
    this.prospectProfileApiService
      .getProspectProfileInfo(memberId)
      .subscribe((data: MemberProfileDto) => {
        this.memberProfile.next(new MemberProfileModel(data));
      });
  }

  review(memberId: number): void {
    this.memberProfileApiService.review(memberId).subscribe((memberInfo) => {
      this.memberProfile.next(new MemberProfileModel(memberInfo));
    });
  }

  status(prospectId: number, prospectStatus: string): void {
    this.prospectProfileApiService.status(prospectId, prospectStatus).subscribe((memberInfo) => {
      this.memberProfile.next(new MemberProfileModel(memberInfo));
    });
  }

  clearMember(): void {
    this.memberProfile.next(null);
    this.importResponse.next(null);
  }

  clearBankIntegrationConfiguration(): void {
    this.bankIntegrationConfigurationSubject.next(null);
  }

  clearAccount(): void {
    this.preselectedAccount.next(null);
  }

  updateConversations(): void {
    this.conversationUpdated.next(true);
  }

  updateTasks(): void {
    this.taskUpdated.next(true);
  }

  updateInteractions(): void {
    this.interactionUpdated.next(true);
  }

  poll(
    memberNumber: string,
    branch: number,
    memberType: string,
  ): Observable<MemberSyncDto> {
    return new Observable<MemberSyncDto>((observer) => {
      const subscription = interval(2500)
        .pipe(
          switchMap(() => this.checkImport(memberNumber, branch, memberType)),
          filter(
            (response: MemberSyncDto) =>
              !response ||
              response.status === 'Success' ||
              response.status === 'Failed'
          )
        )
        .subscribe((response) => {
          observer.next(response);
          observer.complete();
          // cancel the interval
          subscription.unsubscribe();
        });
    });
  }

  prospectPoll(
    memberNumber: string,
    branch: number,
    memberType: string,
  ): Observable<MemberSyncDto> {
    return new Observable<MemberSyncDto>((observer) => {
      const subscription = interval(2500)
        .pipe(
          switchMap(() => this.checkImportProspect(memberNumber, branch, memberType)),
          filter(
            (response: MemberSyncDto) =>
              !response ||
              response.status === 'Success' ||
              response.status === 'Failed'
          )
        )
        .subscribe((response) => {
          observer.next(response);
          observer.complete();
          // cancel the interval
          subscription.unsubscribe();
        });
    });
  }


  importMemberById(memberId: number): void {
    this.spinnerService.showPermanent();

    this.memberProfileApiService
      .importMemberById(memberId)
      .subscribe((data) => {
        this.poll(data.memberNumber, data.branchId, data.memberType)
          .pipe(
            map((response) => new MemberSyncModel(response)),
            finalize(() => {
              this.spinnerService.hidePermanent();
            })
          )
          .subscribe((response) => {
            response.memberId = memberId;
            this.importResponse.next(response);

            if (response.status === 'Failed') {
              const errorMessagePattern = /Member type inconsistency/;
              if (errorMessagePattern.test(response.message)) {
                this.toastService.warning(response.message);
              } else {
                this.toastService.fail(response.message);
              }
            }
          });
      });
  }

  importProspectById(memberId: number): void {
    this.spinnerService.showPermanent();

    this.prospectProfileApiService
      .importProspectById(memberId)
      .subscribe((data) => {
        this.prospectPoll(data.memberNumber, data.branchId, data.memberType)
          .pipe(
            map((response) => new MemberSyncModel(response)),
            finalize(() => {
              this.spinnerService.hidePermanent();
            })
          )
          .subscribe((response) => {
            response.memberId = memberId;
            this.importResponse.next(response);

            if (response.status === 'Failed') {
              this.toastService.fail(response.message);
            }
          });
      });
  }

  import(
    memberNumber: string,
    branch: number,
    memberType: string,
    showPermanentSpinner: boolean = false,
    showToastOnError: boolean = false
  ): void {
    showPermanentSpinner
      ? this.spinnerService.showPermanent()
      : this.spinnerService.show();

    this.memberProfileApiService
      .importMember(memberNumber, branch, memberType)
      .subscribe((data) => {
        if (!this.isProgress && data.status === 'InProgress') {
          this.toastService.success('Member import is in progress');
          this.isProgress = true;
        }
        this.poll(memberNumber, branch, memberType)
          .pipe(
            map((response) => new MemberSyncModel(response)),
            finalize(() => {
              showPermanentSpinner
                ? this.spinnerService.hidePermanent()
                : this.spinnerService.hide();
            })
          )
          .subscribe((response) => {
            response.memberNumber = memberNumber;
            response.memberBranch = branch;
            response.memberType = memberType;
            this.importResponse.next(response);

            if (!showToastOnError && response.status === 'Success') {
              this.toastService.success('Successfully imported');
              this.isProgress = false;
            }
            else if (showToastOnError && response.status === 'Failed') {
              this.toastService.success('Error while importing');
              this.isProgress = false;
            }
          });
      });
  }

  checkImport(
    memberNumber: string,
    branch: number,
    memberType: string
  ): Observable<MemberSyncModel> {
    return this.memberProfileApiService.checkImport(
      memberNumber,
      branch,
      memberType
    );
  }

  checkImportProspect(
    memberNumber: string,
    branch: number,
    memberType: string
  ): Observable<MemberSyncModel> {
    return this.prospectProfileApiService.checkImportProspect(
      memberNumber,
      branch,
      memberType
    );
  }

  getMemberAccount(request: LinkSearchRequestDto): void {
    this.accountsService
      .getAccounts(request)
      .pipe()
      .subscribe((res) => {
        const AccountOptions = [];
        res.data.forEach((item) => {
          const account = new SearchAccountOptionsModel(item);
          AccountOptions.push(account);
        });
        this.preselectedAccount.next(AccountOptions);
      });
  }

  getMemberAccountData(request: LinkSearchRequestDto): Observable<any> {
    return this.accountsService.getAccounts(request).pipe(
      map(res => {
        return res.data;
      })
    );
  }

  getProductTypes(): void {
    this.productService
      .getProductType()
      .pipe(
        map((response: PaginatedResponseDto<ProductTypeDto>) =>
          response.data.map((item) => new ProductTypeModel(item))
        )
      )
      .subscribe((data) => {
        this.productTypes.next(data);
      });
  }

  getProducts(productTypeId?: number): void {
    this.productService
      .getProducts(productTypeId)
      .pipe(
        map((response: PaginatedResponseDto<ProductDto>) =>
          response.data.map((item) => new ProductModel(item))
        )
      )
      .subscribe((data) => {
        this.products.next(data);
      });
  }

  getEcmProductType(): any {
    this.productService
      .getECMProductTypes()
      .pipe(
        map((response: any) =>
          response.map((item) => new ECMProductTypeModel(item))
        )
      )
      .subscribe((data) => {
        this.productTypes.next(data);
      });
  }

  getECMProducts(productType?: string): any {
    this.productService
      .getECMProduct(productType)
      .pipe(
        map((response: any) =>
          response.map((item) => new ECMProductModel(item))
        )
      )
      .subscribe((data) => {
        this.products.next(data);
      });
  }


  getConcernType(concernTypeId?: number): void {
    this.conversationService
      .getConcernType(concernTypeId)
      .pipe(
        map((response: PaginatedResponseDto<ConcernTypeDto>) =>
          response.data.map((item) => new ConcernTypeModel(item))
        )
      )
      .subscribe((data) => {
        this.concernType.next(data);
      });
  }
  getConcernTypes(): void {
    this.conversationService
      .getConcernTypes()
      .pipe(
        map((response: PaginatedResponseDto<ConcernTypeDto>) =>
          response.data.map((item) => new ConcernTypeModel(item))
        )
      )
      .subscribe((data) => {
        this.concernTypes.next(data);
      });
  }

  getCampaigns(active: boolean = true): void {
    const request = {
      status: !!active ? 'active' : active === false ? 'inactive' : null,
    };

    this.productService
      .getCampaigns(request)
      .pipe(
        map((response: PaginatedResponseDto<CampaignsDto>) =>
          response.data.map((item) => new CampaignsModel(item))
        )
      )
      .subscribe((data) => {
        this.campaigns.next(data);
      });
  }

  getBranch(active: boolean = true): void {
    this.asappApiService
      .getUserBranch()
      .pipe(
        map((response: UserBranchDto[]) =>
          response.map((item) => new UserBranchModel(item))
        )
      )
      .subscribe((data) => {
        this.userBranches.next(data);
      });
  }

  loadBankIntegrationConfiguration(): void {
    this.memberProfileApiService
      .getBankIntegrationConfiguration()
      .subscribe((config) => {
        this.bankIntegrationConfigurationSubject.next(config);
      });
  }

  loadCoreBankingIntegrationList(): void {
    this.memberProfileApiService
      .getCoreBankingIntegrationList()
      .subscribe((config) => {
        this.coreBankingIntegrationListSubject.next(config);
      });
  }

  loadECMUsageKey(): void {
    this.offerApiService.getOfferSiteDetails();
    this.ECMUsageKeyLoading = true;

    this.keyVaultService
      .getECMUsageKey()
      .pipe(finalize(() => (this.ECMUsageKeyLoading = false)))
      .subscribe((ECMVisible) => this.ECMUsageKeySubject.next(ECMVisible));
  }

  loadSqlBuilderUsageKey(): void {
    this.sqlBuilderUsageKeyLoading = true;

    this.keyVaultService
      .getSqlBuilderUsageKey()
      .pipe(finalize(() => (this.sqlBuilderUsageKeyLoading = false)))
      .subscribe((sqlBuilderPageVisible) =>
        this.sqlBuilderPageVisibleSubject.next(sqlBuilderPageVisible)
      );
  }

  loadThemeBuilderKey(): void {
    this.themeBuilderTypeKeyLoading = true;

    this.keyVaultService
      .getThemeBuilderTypeKey()
      .pipe(finalize(() => (this.themeBuilderTypeKeyLoading = false)))
      .subscribe((themeBuilderType) =>
        this.themeBuilderTypeKeySubject.next(themeBuilderType)
      );
  }

  loadUmbrellaVersion(): void {
    // this.UmbrellaVersionSubject.next(this.keyVaultService.getUmbrellaVersion())
    this.keyVaultService
      .getUmbrellaVersion()
      .pipe(finalize(() => (this.UmbrellaVersionLoding = null)))
      .subscribe((VersionVisible) =>
        this.UmbrellaVersionSubject.next(VersionVisible)
      );
  }

  loadPendoUsageKey(): void {
    this.keyVaultService
      .getPendoApiKey()
      .pipe(finalize(() => (this.PendoUsageKeyLoading = null)))
      .subscribe((PendoKey) =>
        this.PendoUsageKeySubject.next(PendoKey)
      );
  }

  loadPowerBiUrl(): void {
    this.offerEnableLogic$.pipe(take(1)).subscribe((value) => {
      const isOfferOnly = value === 3 || value === 0;
      if (!isOfferOnly) {
        this.powerBiUrlSubject.next('');
        return;
      }

      this.personApiService
        .getPowerBiUrl()
        .pipe(take(1))
        .subscribe(
          (powerBiUrl) => this.powerBiUrlSubject.next((powerBiUrl ?? '').trim()),
          () => this.powerBiUrlSubject.next('')
        );
    });
  }

  getSiteDetails(): any {
    return this.asappApiService.getSiteDetails();
  }

  getOfferSite(): void {
    if (offerBase !== undefined) {
      this.offerApiService.getOfferSiteDetails().subscribe((data) => {
        if (data.isEnableOffer && !data.isEnableAOSLOS && !data.isEnableCRM) {
          this.offerSiteDetail.next(false);
        } else if (
          data.isEnableOffer &&
          data.isEnableAOSLOS &&
          !data.isEnableCRM
        ) {
          this.offerSiteDetail.next(true);
        } else {
          this.offerSiteDetail.next(true);
        }
      });
    } else {
      this.offerSiteDetail.next(true);
    }
  }

  getOfferEnableLogic(): void {
    if (offerBase !== undefined) {
      this.offerApiService.getOfferSiteDetails().subscribe(
        (data) => {
          if (data.isEnableOffer && !data.isEnableAOSLOS && !data.isEnableCRM) {
            this.offerEnableLogic.next(1);
          } else if (
            data.isEnableOffer &&
            data.isEnableAOSLOS &&
            !data.isEnableCRM
          ) {
            this.offerEnableLogic.next(2);
          } else if (
            !data.isEnableOffer &&
            data.isEnableAOSLOS &&
            data.isEnableCRM
          ) {
            this.offerEnableLogic.next(3);
          } else {
            this.offerEnableLogic.next(0);
          }
        },
        (error: HttpErrorResponse) => {
          this.offerEnableLogic.next(3);
        }
      );
    } else {
      this.offerEnableLogic.next(3);
    }
  }

  getDecision(applicationId: number, applicationProductId: number): void {
    const requestPayload = {
      applicationId,
      applicationProductId
    };

    this.applicationService.getLendingDecision(requestPayload).pipe(
      map((response: ApiResponse<any>) => response.result || [])
    ).subscribe((decisionValue: any) => {
      this.autoDecisionSubject.next(decisionValue);
    });
  }

  getRateAutoDecision(applicationId: number, applicationProductId: number){
    const requestPayload = {
      requestId: crypto.randomUUID(),
      applicationId,
      applicationProductId
    };
    this.applicationService.getRateAutoDecision(requestPayload).pipe(
      map((response: any) => {
        return response.result || [];
      })
    ).subscribe((rateAutoDecisionValue) => {
      this.rateAutoDecisionSubject.next(rateAutoDecisionValue);
    });
  }

  getRateCurrentVersion(request: any){
    this.applicationService.getRateCurrentVersion(request).pipe(
      map((response: any) => {
        return response.result || [];
      })
    ).subscribe((rateCurrentVersionValue) => {
      this.rateVersionSubject.next(rateCurrentVersionValue);
    });
  }

  clearRateVersion() {
    this.rateVersionSubject.next(null);
  }

  getApprovalCurrentVersion(request: any){
    this.applicationService.getApprovalCurrentVersion(request).pipe(
      map((response: any) => {
        return response.result || [];
      })
    ).subscribe((approvalCurrentVersionValue) => {
      this.approvalVersionSubject.next(approvalCurrentVersionValue);
    });
  }

  clearApprovalVersion() {
    this.approvalVersionSubject.next(null);
  }

  getOfferLicence(): void {
    if (offerBase !== undefined) {
      this.offerApiService.getOfferSiteDetails().subscribe(
        (data) => {
          this.isOfferLicence.next(data.isEnableOffer);
        },
        (error: HttpErrorResponse) => {
          this.isOfferLicence.next(false);
        }
      );
    } else {
      this.isOfferLicence.next(false);
    }
  }

  sendBulkEmails(CRMType, emailAddresses, userGroup): void {
    const emailsBulk = [];
    emailAddresses.forEach(data => {
      const sendRequest = new SendEmailRequestModel();
      sendRequest.from.address = this.userEmail;
      sendRequest.from.name = `${this.user.firstName} ${this.user.lastName}`;
      sendRequest.subject = 'New Assignment';
      sendRequest.body = 'A new or existing ' + CRMType + ' has been assigned to ' + userGroup + ' Group. Please sign into the Team portal to review.';
      sendRequest.data = null;
      sendRequest.to.address = data;
      emailsBulk.push(sendRequest);
    });
    this.sendBulk(emailsBulk).subscribe((message) => {
      if (message) { }
    });
  }

  sendBulk(request): Observable<SendEmailRequestDto> {
    return this.emailsService.sendBulk(request);
  }

  send(request: SendEmailRequestDto): Observable<SendEmailRequestDto> {
    return this.emailsService.send(request);
  }

  SendEmail(request, CRMType): void {
    const sendRequest = new SendEmailRequestModel();
    sendRequest.from.address = this.userEmail;
    sendRequest.from.name = `${this.user.firstName} ${this.user.lastName}`;
    sendRequest.subject = 'New Assignment';
    sendRequest.body = 'A new or existing ' + CRMType + ' has been assigned to you. Please sign into the Team portal to review.';
    sendRequest.data = null;
    sendRequest.to.address = request.to.address;
    sendRequest.entityType = request.entityType || CRMType;
    sendRequest.entityId = request.entityId;
    this.send(sendRequest).subscribe((message) => {
      if (message) { }
    });
  }

  sendCRMImportBulkEmailUsers(CRMType, emailAddresses, importId): void {
    const emailsBulk = [];
    emailAddresses.forEach(data => {
      const sendRequest = new SendEmailRequestModel();
      sendRequest.from.address = this.userEmail;
      sendRequest.from.name = `${this.user.firstName} ${this.user.lastName}`;
      sendRequest.subject = 'New Assignment';
      sendRequest.body = `One or more ${CRMType}s have been assigned to you via Import ${importId}. Please sign into the Team portal to review.`;
      sendRequest.data = null;
      sendRequest.to.address = data;
      emailsBulk.push(sendRequest);
    });
    this.sendBulk(emailsBulk).subscribe((message) => {
      if (message) { }
    });
  }

  sendCRMImportBulkEmailGroups(emailsBulk): void {
    emailsBulk.forEach(data => {
      data.from.address = this.userEmail;
      data.from.name = `${this.user.firstName} ${this.user.lastName}`;
    });
    this.sendBulk(emailsBulk).subscribe((message) => {
      if (message) { }
    });
  }

  getProductsData(productTypeId?: number): Observable<ProductModel[]> {
    return this.productService
      .getProducts(productTypeId)
      .pipe(
        map((response: PaginatedResponseDto<ProductDto>) =>
          response.data.map((item) => new ProductModel(item))
        )
      );
  }

  removeRouteState(): void {
    sessionStorage.removeItem(this.conversationRoutePath);
    sessionStorage.removeItem(this.salesRoutePath);
    sessionStorage.removeItem(this.interactionRoutePath);
    sessionStorage.removeItem(this.NotesRoutePath);
    sessionStorage.removeItem(this.TaskRoutePath);
  }
  getApplicationDetails(applicationId: number): void {
    this.applicationService.getApplicationSummary(applicationId)
    .subscribe((response: ApplicationApiResponse<ApplicationMenuViewItemDto>) => {
      const application = response.result;
      this.applicationView.next(new ApplicationMenuViewItemModel(application));
    });
  }
  getAssetByApplicationId(applicationId: number): void {
  this.applicationService.getAssetByApplicationId(applicationId)
    .subscribe((response: ApiResponse<ApplicationAssetResponse[]>) => {
      const assetList = response?.result;
      if (assetList && assetList.length > 0) {
        const asset = assetList[0]; // ✅ Use first item
        this.aseetdetails.next(new ApplicationAssetModel(asset));
      }
    });
}
  getAllAssetTypes(): void {
    this.applicationService.getAllAssetTypes()
    .subscribe((response: ApiResponse<AssetCategoryResponse[]>) => {
      const assetTypes = response.result;
      this.allAssetTypes.next(assetTypes);
    });
  }
    getEquifaxConfiguration(): void {
    this.asappApiService.getEquifaxConfiguration().subscribe({
    next: (config: boolean) => {
      this.equifaxConfigurationSubject.next(config);
    },
    error: (err) => {
      console.error('Failed to fetch Equifax configuration:', err);
      this.equifaxConfigurationSubject.next(false); // or handle accordingly
    }
  });
  }

addApplicationLog(payload: any) {
  return this.asappApiService.addApplicationLog(payload);
}

}
